import React from 'react';
import {
  Card, CardContent, Typography, Box, LinearProgress, Chip, Button, Divider, Stack
} from '@mui/material';
import {
  Rocket as RocketIcon,
  Timer as TimerIcon,
  People as PeopleIcon,
  Store as StoreIcon,
  LocalShipping as DeliveryIcon,
  GpsFixed as GpsIcon,
  Api as ApiIcon,
  Warning as WarningIcon
} from '@mui/icons-material';

const statusColors = {
  TRIAL: { bg: '#FFF7ED', color: '#F59E0B', label: 'Prueba Gratuita' },
  ACTIVA: { bg: '#ECFDF5', color: '#10B981', label: 'Activa' },
  VENCIDA: { bg: '#FEF2F2', color: '#EF4444', label: 'Vencida' },
  CANCELADA: { bg: '#F5F5F5', color: '#9E9E9E', label: 'Cancelada' },
  SUSPENDIDA: { bg: '#FFF3E0', color: '#FF9800', label: 'Suspendida' },
};

export default function MiPlanCard({ data, onUpgrade }) {
  if (!data) return null;

  const { suscripcion, plan, limites, features } = data;
  const status = statusColors[suscripcion.estado] || statusColors.TRIAL;

  const porcentajeServicios = limites.max_servicios_mes
    ? Math.min(100, (limites.servicios_consumidos / limites.max_servicios_mes) * 100)
    : 0;

  const porcentajeTecnicos = limites.max_tecnicos
    ? Math.min(100, (limites.tecnicos_activos / limites.max_tecnicos) * 100)
    : 0;

  return (
    <Card sx={{ borderRadius: 3, border: `2px solid ${status.color}20`, overflow: 'visible' }}>
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight={800} color="#1E2783">
              {plan.nombre}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {plan.codigo === 'FREE' ? 'Prueba gratuita' : `${suscripcion.tipo_periodo === 'ANUAL' ? 'Anual' : 'Mensual'}`}
            </Typography>
          </Box>
          <Chip
            label={status.label}
            sx={{ bgcolor: status.bg, color: status.color, fontWeight: 700, fontSize: 13 }}
          />
        </Box>

        {/* Trial countdown */}
        {suscripcion.estado === 'TRIAL' && suscripcion.dias_restantes !== null && (
          <Box sx={{ bgcolor: '#FFF7ED', borderRadius: 2, p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <TimerIcon sx={{ color: '#F59E0B' }} />
            <Box>
              <Typography variant="body2" fontWeight={700} color="#92400E">
                {suscripcion.dias_restantes} días restantes de prueba
              </Typography>
              <Typography variant="caption" color="#B45309">
                Vence el {new Date(suscripcion.fecha_fin).toLocaleDateString()}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Vencida warning */}
        {data.necesita_upgrade && (
          <Box sx={{ bgcolor: '#FEF2F2', borderRadius: 2, p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon sx={{ color: '#EF4444' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight={700} color="#991B1B">
                Tu plan ha vencido
              </Typography>
              <Typography variant="caption" color="#B91C1C">
                Elige un plan para seguir creando servicios
              </Typography>
            </Box>
            <Button variant="contained" size="small" onClick={onUpgrade} sx={{ bgcolor: '#EF4444', '&:hover': { bgcolor: '#DC2626' } }}>
              Upgrade
            </Button>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Consumo de servicios */}
        {limites.max_servicios_mes && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" fontWeight={600}>Servicios este período</Typography>
              <Typography variant="body2" fontWeight={700} color={porcentajeServicios > 80 ? '#EF4444' : '#1E2783'}>
                {limites.servicios_consumidos} / {limites.max_servicios_mes}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={porcentajeServicios}
              sx={{
                height: 8, borderRadius: 4,
                bgcolor: '#E2E8F0',
                '& .MuiLinearProgress-bar': {
                  bgcolor: porcentajeServicios > 80 ? '#EF4444' : '#1E2783',
                  borderRadius: 4,
                },
              }}
            />
          </Box>
        )}

        {/* Consumo de técnicos */}
        {limites.max_tecnicos && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" fontWeight={600}>Técnicos activos</Typography>
              <Typography variant="body2" fontWeight={700} color={porcentajeTecnicos > 80 ? '#EF4444' : '#1E2783'}>
                {limites.tecnicos_activos} / {limites.max_tecnicos}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={porcentajeTecnicos}
              sx={{
                height: 8, borderRadius: 4,
                bgcolor: '#E2E8F0',
                '& .MuiLinearProgress-bar': {
                  bgcolor: porcentajeTecnicos > 80 ? '#EF4444' : '#1E2783',
                  borderRadius: 4,
                },
              }}
            />
          </Box>
        )}

        {/* Features */}
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
          <Chip icon={<PeopleIcon />} label={limites.max_tecnicos ? `${limites.max_tecnicos} técnicos` : 'Técnicos ilimitados'} size="small" variant="outlined" />
          <Chip icon={<StoreIcon />} label={limites.max_locales ? `${limites.max_locales} local${limites.max_locales > 1 ? 'es' : ''}` : 'Locales ilimitados'} size="small" variant="outlined" />
          {features.gps && <Chip icon={<GpsIcon />} label="GPS" size="small" color="primary" variant="outlined" />}
          {features.delivery && <Chip icon={<DeliveryIcon />} label="Delivery" size="small" color="success" variant="outlined" />}
          {features.api && <Chip icon={<ApiIcon />} label="API" size="small" color="info" variant="outlined" />}
        </Stack>

        {/* Upgrade button */}
        {plan.codigo !== 'ENTERPRISE' && suscripcion.estado !== 'VENCIDA' && (
          <Button
            fullWidth
            variant="outlined"
            startIcon={<RocketIcon />}
            onClick={onUpgrade}
            sx={{ mt: 2, borderColor: '#1E2783', color: '#1E2783', fontWeight: 600, borderRadius: 2 }}
          >
            {plan.codigo === 'FREE' ? 'Elegir un plan' : 'Cambiar plan'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
